/**
 * Call-lifecycle telemetry for voice — the web half of the funnel.
 *
 * A voice call used to leave no server-side trace unless the model happened to invoke a tool, so a
 * call that connected fine, one that never connected, and one that died were indistinguishable in
 * production. `v1/voice/session` now logs the start (the denominator); this reports the two states
 * only the client knows — that audio actually started flowing, and how the call stopped — plus a
 * recoverable audio fault the call survived.
 *
 * Everything here is best-effort by construction: see `reportVoiceLifecycle`.
 */

/** Mirrors the closed set the harness enumerates. Keep the two in step, or a reason logs as `unknown`. */
export type VoiceEndReason =
  | 'user_ended'
  | 'agent_ended'
  | 'error'
  | 'cap_reached'
  | 'socket_drop'
  | 'permission_denied'
  | 'no_microphone'
  | 'unknown';

export type VoiceFault = 'capture_chain_rebuilt' | 'player_reconnected' | 'conversion_failed' | 'mic_silent' | 'audio_route_lost' | 'unknown';

/**
 * How close to the ceiling still counts as having run into it — the gap between the server closing
 * the socket and the client getting round to reporting. Native's own tolerance, to the millisecond.
 */
export const CAP_REACHED_TOLERANCE_MS = 2_000;

/**
 * Did this call actually run into its duration ceiling?
 *
 * ⚠️ **DERIVED, not flagged** — the sentence both phones already carry at their own call sites
 * (`VoiceCallController.reportEnd`, iOS and Android): *"the cap is only interesting if the call
 * actually ran into it, and a flag set when the ceiling was ANNOUNCED would mark every long call as
 * capped."* Web was the one surface that flagged it, from `onDurationCap` — which fires once per
 * MINT, at the START of the call, to publish the ceiling for the clock. Since `cap_reached` outranks
 * `user_ended` below, EVERY web call that successfully minted a token filed itself as capped: a
 * three-second hang-up and a genuine five-minute cap were the same row, and "is web voice dying
 * under people, or are they hanging up?" had no answer at all.
 *
 * Extracted here rather than inlined so the three surfaces group the same calls the same way — a
 * funnel joined across surfaces is only as good as the predicate behind its `reason`. Change this
 * and you change three apps: the two native derivations are the reference.
 */
export function capWasReached(durationMs: number | undefined, maxCallDurationMs: number | null): boolean {
  if (!maxCallDurationMs || durationMs === undefined) return false;
  return durationMs >= maxCallDurationMs - CAP_REACHED_TOLERANCE_MS;
}

/**
 * Why the call stopped, from what the surface knows at the moment it stops.
 *
 * Pure so the classification can be pinned without a socket. The ORDER matters: a user who hangs up
 * during a failure still ended the call, but the failure is the more useful fact — so an error
 * outranks the hang-up, and only a clean stop is attributed to the person.
 */
export function voiceEndReason(input: {
  /** The user pressed End (as opposed to the model hanging up or the transport dying). */
  userInitiated: boolean;
  status: 'ended' | 'error' | 'idle';
  /** The server's duration cap was reached — the call was wound down, not broken. */
  capReached?: boolean;
  /** Whatever the transport said. A close code is the only part we can classify. */
  errorDetail?: string | null;
}): VoiceEndReason {
  if (input.status === 'error') {
    const detail = input.errorDetail ?? '';
    // A socket that closed under us is a different operational story from the model or a tool
    // failing, and it is the one that correlates with network and token problems.
    if (/\b(1006|1007|1011|socket|websocket|closed)\b/i.test(detail)) return 'socket_drop';
    if (/\b(permission|notallowed|denied)\b/i.test(detail)) return 'permission_denied';
    if (/\b(no ?mic|microphone|notfound|audio ?device)\b/i.test(detail)) return 'no_microphone';
    return 'error';
  }
  if (input.capReached) return 'cap_reached';
  if (input.userInitiated) return 'user_ended';
  if (input.status === 'ended') return 'agent_ended';
  return 'unknown';
}

export interface VoiceLifecycleReport {
  event: 'connected' | 'ended' | 'fault';
  callId: string;
  conversationId?: string;
  connectMs?: number;
  durationMs?: number;
  turns?: number;
  reason?: VoiceEndReason;
  errorCode?: string;
  fault?: VoiceFault;
  /**
   * How the reconnect path actually behaved, so a socket death is diagnosable from the server.
   *
   * These three existed only as a browser `console.warn`, which is why a call that died on a spent
   * credential and one that opened a fresh session on drifted constraints were indistinguishable in
   * production — both surface as the same close code. `opens` > 1 on a single-use credential is the
   * shape that ends in 1011.
   */
  opens?: number;
  /** Every reconnect this call attempted, cumulative — `resumeAttempts` is the budget and resets. */
  reconnects?: number;
  resumeAttempts?: number;
  serverResumable?: boolean;
}

/**
 * Send one report. Never throws, never awaits anything the caller cares about.
 *
 * `keepalive` because the most valuable report is the LAST one — a call ends when the user closes
 * the tab as often as when they press End, and an ordinary fetch is cancelled on unload, which
 * would bias the data toward exactly the calls that went well.
 */
export function reportVoiceLifecycle(report: VoiceLifecycleReport): void {
  try {
    void fetch('/api/voice/lifecycle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // A surface that cannot report is still a surface that can take a call.
  }
}
